import { useEffect } from "react"
import { Button, DatePicker, Drawer, Form, Input, Space, Switch } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { LeadFormData, LeadModalProps } from "../types"
import { LEAD_VALIDATION } from "../constants"
import ResponsiveSelect from "@/components/ResponsiveSelect"

const { TextArea } = Input

interface InternalFormData {
  studentName?: string
  leadAt?: Dayjs
  sourceType?: string
  retentionType?: string
  wechatAdded: boolean
  storeEvaluated: boolean
  evaluationNote?: string
  trialLessonNote?: string
  note?: string
}

const LeadModal = ({
  isOpen,
  onClose,
  onSave,
  lead,
  loading = false,
  sourceOptions,
  retentionTypeOptions,
  optionsLoading = false,
}: LeadModalProps) => {
  const [form] = Form.useForm<InternalFormData>()

  useEffect(() => {
    form.resetFields()

    if (isOpen && lead) {
      form.setFieldsValue({
        studentName: lead.studentName,
        leadAt: dayjs(lead.leadAt),
        sourceType: lead.sourceType,
        retentionType: lead.retentionType,
        wechatAdded: lead.wechatAdded ?? false,
        storeEvaluated: lead.storeEvaluated ?? false,
        evaluationNote: lead.evaluationNote,
        trialLessonNote: lead.trialLessonNote,
        note: lead.note,
      })
      return
    }

    if (isOpen) {
      form.setFieldsValue({
        studentName: undefined,
        leadAt: dayjs(),
        sourceType: undefined,
        retentionType: undefined,
        wechatAdded: false,
        storeEvaluated: false,
        evaluationNote: undefined,
        trialLessonNote: undefined,
        note: undefined,
      })
    }
  }, [form, isOpen, lead])

  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData: LeadFormData = {
      studentName: values.studentName?.trim() || undefined,
      leadAt: values.leadAt!.toISOString(),
      sourceType: values.sourceType!.trim(),
      retentionType: values.retentionType?.trim() || undefined,
      wechatAdded: values.wechatAdded,
      storeEvaluated: values.storeEvaluated,
      evaluationNote: values.evaluationNote?.trim() || undefined,
      trialLessonNote: values.trialLessonNote?.trim() || undefined,
      note: values.note?.trim() || undefined,
    }
    onSave(formData)
  }

  return (
    <Drawer
      title={lead ? "编辑客资" : "新增客资"}
      open={isOpen}
      onClose={onClose}
      width={720}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            确定
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" preserve={false} className="lead-modal-form">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <Form.Item name="studentName" label="学生姓名" className="mb-0">
            <Input placeholder="请输入学生姓名（可选）" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="leadAt"
            label="日期"
            rules={[{ required: true, message: LEAD_VALIDATION.LEAD_AT_REQUIRED }]}
            className="mb-0"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: "100%" }}
              placeholder="请选择客资日期时间"
            />
          </Form.Item>
          <Form.Item
            name="sourceType"
            label="来源"
            rules={[{ required: true, message: LEAD_VALIDATION.SOURCE_REQUIRED }]}
            className="mb-0"
          >
            <ResponsiveSelect
              options={sourceOptions}
              placeholder="请选择来源"
              disabled={optionsLoading}
              loading={optionsLoading}
            />
          </Form.Item>
          <Form.Item name="retentionType" label="留资形式" className="mb-0">
            <ResponsiveSelect
              options={retentionTypeOptions}
              placeholder="请选择留资形式"
              allowClear
              disabled={optionsLoading}
              loading={optionsLoading}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-2">
          <Form.Item
            name="wechatAdded"
            label="是否加微信"
            valuePropName="checked"
            initialValue={false}
            className="mb-0"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            name="storeEvaluated"
            label="是否到店评估"
            valuePropName="checked"
            initialValue={false}
            className="mb-0"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </div>

        <Form.Item name="evaluationNote" label="评估备注" className="mt-3 mb-0">
          <TextArea placeholder="请输入评估备注（可选）" rows={2} maxLength={500} />
        </Form.Item>

        <Form.Item name="trialLessonNote" label="体验课备注" className="mt-3 mb-0">
          <TextArea placeholder="请输入体验课备注（可选）" rows={2} maxLength={500} />
        </Form.Item>

        <Form.Item name="note" label="备注" className="mt-3 mb-0">
          <TextArea
            placeholder="请输入备注（可选）"
            rows={2}
            maxLength={LEAD_VALIDATION.NOTE_MAX_LENGTH}
            showCount
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

export default LeadModal
